import React, { useState, useEffect } from 'react';
import axios from 'axios';
import JSON5 from 'json5';

const ToolInstanceManager = ({ focusedToolId = null, toolEditId = null, onClearFocus, onClearToolEdit }) => {
  const [toolTypes, setToolTypes] = useState([]);
  const [toolInstances, setToolInstances] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    description: '',
    tool_type: '',
    configuration: {},
    tags: '',
    is_favorite: false
  });
  const [configForm, setConfigForm] = useState('{}');
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [configFields, setConfigFields] = useState([]);
  const [configValues, setConfigValues] = useState({});
  const [jsonDraftValues, setJsonDraftValues] = useState({});
  const [extraConfigEntries, setExtraConfigEntries] = useState([]);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [isRawConfigMode, setIsRawConfigMode] = useState(false);
  const [configError, setConfigError] = useState('');
  const [fieldsCache, setFieldsCache] = useState({});
  const [activeToolMetadata, setActiveToolMetadata] = useState(null);

  useEffect(() => {
    if (!focusedToolId) {
      return;
    }

    // Ensure the list view is visible
    setIsCreating(false);
    setEditingTool(null);

    const focusTool = toolInstances.find(tool => tool.id === focusedToolId);
    if (focusTool) {
      // Small timeout to allow DOM to render
      requestAnimationFrame(() => {
        const cardElement = document.getElementById(`tool-card-${focusTool.id}`);
        if (cardElement) {
          cardElement.classList.add('highlight');
          cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => cardElement.classList.remove('highlight'), 1600);
        }
      });
    }

    if (onClearFocus) {
      onClearFocus();
    }
  }, [focusedToolId, toolInstances, onClearFocus]);

  useEffect(() => {
    fetchToolTypes();
    fetchToolInstances();
  }, []);

  useEffect(() => {
    if (!toolEditId || toolInstances.length === 0) {
      return;
    }

    const toolToEdit = toolInstances.find(tool => tool.id === toolEditId);
    if (!toolToEdit) {
      return;
    }

    handleEdit(toolToEdit);

    if (onClearToolEdit) {
      onClearToolEdit();
    }
  }, [toolEditId, toolInstances, onClearToolEdit]);

  const fetchToolTypes = async () => {
    try {
      const response = await axios.get('/api/tool-types/');
      // Handle both paginated and non-paginated responses
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setToolTypes(data);
    } catch (err) {
      setError('Failed to load tool types');
      console.error('Error fetching tool types:', err);
    }
  };

  const fetchToolInstances = async () => {
    try {
      const response = await axios.get('/api/tool-instances/');
      // Handle both paginated and non-paginated responses
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setToolInstances(data);
    } catch (err) {
      setError('Failed to load tool instances');
      console.error('Error fetching tool instances:', err);
    }
  };

  const resetConfigState = () => {
    setConfigFields([]);
    setConfigValues({});
    setJsonDraftValues({});
    setExtraConfigEntries([]);
    setConfigForm('{}');
    setIsRawConfigMode(false);
    setConfigError('');
    setActiveToolMetadata(null);
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setEditingTool(null);
    setFormData({
      name: '',
      short_name: '',
      description: '',
      tool_type: '',
      configuration: {},
      tags: '',
      is_favorite: false
    });
    resetConfigState();
    setError('');
  };

  const handleEdit = (tool) => {
    resetConfigState();
    setEditingTool(tool);
    setIsCreating(true);
    setFormData({
      name: tool.name,
      short_name: tool.short_name,
      description: tool.description || '',
      tool_type: tool.tool_type,
      configuration: tool.configuration || {},
      tags: tool.tags || '',
      is_favorite: tool.is_favorite
    });
    setError('');
    const config = tool.configuration || {};
    setConfigForm(JSON.stringify(config, null, 2));

    const typeCode = tool.tool_type_code || toolTypes.find(t => t.id === tool.tool_type)?.type_code;
    if (typeCode) {
      fetchConfigFields(typeCode, config);
      const matchedType = toolTypes.find(t => t.type_code === typeCode || t.id === tool.tool_type);
      if (matchedType) {
        setSelectedType(matchedType);
      }
    } else {
      resetConfigState();
      setIsRawConfigMode(true);
      setConfigError('Unable to load structured editor for this tool type. Editing in raw JSON mode.');
    }
  };

  const fetchConfigFields = async (typeCode, initialConfig = null) => {
    if (!typeCode) {
      resetConfigState();
      return;
    }

    if (fieldsCache[typeCode]) {
      const cached = fieldsCache[typeCode];
      applyConfigMetadata(cached, initialConfig);
      return;
    }

    try {
      setIsConfigLoading(true);
      const response = await axios.get(`/api/tool-types/all_metadata/`);
      const metadata = response.data || [];
      const match = metadata.find(item => item.code === typeCode);
      if (match) {
        setFieldsCache(prev => ({ ...prev, [typeCode]: match }));
        applyConfigMetadata(match, initialConfig);
      } else {
        resetConfigState();
        setIsRawConfigMode(true);
        setConfigError('No structured configuration available. Please use raw JSON mode.');
      }
    } catch (err) {
      console.error('Error loading config fields:', err);
      resetConfigState();
      setIsRawConfigMode(true);
      setConfigError('Failed to load configuration fields. Switch to raw JSON if needed.');
    } finally {
      setIsConfigLoading(false);
    }
  };

  const applyConfigMetadata = (metadata, initialConfig = null) => {
    const fields = metadata?.fields || [];
    const sourceConfig = initialConfig ? { ...initialConfig } : {};
    const usedKeys = new Set();
    const values = {};
    const jsonDrafts = {};

    fields.forEach(field => {
      usedKeys.add(field.name);
      let value = sourceConfig[field.name];

      if (value === undefined) {
        if (field.default !== undefined) {
          value = field.default;
        } else {
          switch (field.type) {
            case 'boolean':
              value = false;
              break;
            case 'number':
              value = '';
              break;
            case 'json':
              value = [];
              break;
            default:
              value = '';
          }
        }
      }

      if (field.type === 'number' && value !== '' && value !== null && value !== undefined) {
        const numericValue = Number(value);
        value = Number.isNaN(numericValue) ? '' : numericValue;
      }

      if (field.type === 'boolean') {
        value = Boolean(value);
      }

      if (field.type === 'json') {
        const serialized = value !== undefined ? JSON.stringify(value, null, 2) : '';
        jsonDrafts[field.name] = serialized;
        try {
          value = serialized ? JSON5.parse(serialized) : [];
        } catch (err) {
          value = [];
        }
      }

      values[field.name] = value;
      delete sourceConfig[field.name];
    });

    const extras = Object.entries(sourceConfig).map(([key, value], index) => ({
      id: `extra-${key}-${index}-${Date.now()}`,
      key,
      value: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)
    }));

    setActiveToolMetadata(metadata);
    setConfigFields(fields);
    setConfigValues(values);
    setJsonDraftValues(jsonDrafts);
    setExtraConfigEntries(extras);
    setIsRawConfigMode(false);
    setConfigError('');

    try {
      const previewConfig = buildConfigurationFromState(fields, values, jsonDrafts, extras, false);
      setConfigForm(JSON.stringify(previewConfig, null, 2));
    } catch (err) {
      // Ignore preview errors here
    }
  };

  const handleConfigFieldChange = (field, rawValue) => {
    let value = rawValue;

    if (field.type === 'number') {
      value = rawValue === '' ? '' : Number(rawValue);
    }

    if (field.type === 'boolean') {
      value = Boolean(rawValue);
    }

    setConfigValues(prev => ({ ...prev, [field.name]: value }));
    setConfigError('');
  };

  const handleJsonFieldChange = (field, text) => {
    setJsonDraftValues(prev => ({ ...prev, [field.name]: text }));
    if (!text.trim()) {
      setConfigValues(prev => ({ ...prev, [field.name]: [] }));
      setConfigError('');
      return;
    }

    try {
      const parsed = JSON5.parse(text);
      setConfigValues(prev => ({ ...prev, [field.name]: parsed }));
      setConfigError('');
    } catch (err) {
      setConfigError(`Invalid JSON for field "${field.name}"`);
    }
  };

  const handleAddExtraEntry = () => {
    setExtraConfigEntries(prev => ([
      ...prev,
      {
        id: `extra-${Date.now()}-${prev.length}`,
        key: '',
        value: ''
      }
    ]));
  };

  const handleExtraEntryChange = (id, field, rawValue) => {
    setExtraConfigEntries(prev => prev.map(entry => (
      entry.id === id ? { ...entry, [field]: rawValue } : entry
    )));
  };

  const handleRemoveExtraEntry = (id) => {
    setExtraConfigEntries(prev => prev.filter(entry => entry.id !== id));
  };

  const buildConfigurationFromState = (fields, values, jsonDraftsState, extraEntriesState, shouldValidate = true) => {
    const config = {};

    fields.forEach(field => {
      let value = values[field.name];

      if (field.type === 'json') {
        const draft = jsonDraftsState[field.name] || '';
        if (!draft.trim()) {
          value = [];
        } else {
          try {
            value = JSON5.parse(draft);
          } catch (err) {
            if (shouldValidate) {
              setConfigError(`Invalid JSON for field "${field.name}"`);
              throw err;
            }
            value = null;
          }
        }
      }

      if (value === '' || value === undefined) {
        return;
      }

      config[field.name] = value;
    });

    extraEntriesState.forEach(entry => {
      const key = entry.key?.trim();
      if (!key) {
        return;
      }

      const rawValue = entry.value;
      if (rawValue === undefined) {
        return;
      }

      let parsedValue = rawValue;
      if (typeof rawValue === 'string' && rawValue.trim() !== '') {
        try {
          parsedValue = JSON5.parse(rawValue);
        } catch (err) {
          // leave as string
        }
      }
      config[key] = parsedValue;
    });

    return config;
  };

  const buildConfiguration = () => {
    try {
      const config = buildConfigurationFromState(
        configFields,
        configValues,
        jsonDraftValues,
        extraConfigEntries,
        true
      );
      setConfigForm(JSON.stringify(config, null, 2));
      return config;
    } catch (error) {
      if (!configError) {
        setConfigError('Failed to build configuration object');
      }
      throw error;
    }
  };

  const formatFieldLabel = (name) => {
    if (!name) return '';
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleToggleRawMode = () => {
    if (!activeToolMetadata) {
      setIsRawConfigMode(true);
      return;
    }

    if (isRawConfigMode) {
      try {
        const parsed = JSON5.parse(configForm || '{}');
        applyConfigMetadata(activeToolMetadata, parsed);
        setIsRawConfigMode(false);
        setConfigError('');
      } catch (err) {
        setConfigError('Invalid JSON in raw editor. Fix the JSON before switching back to the structured editor.');
      }
    } else {
      try {
        const config = buildConfiguration();
        setConfigForm(JSON.stringify(config, null, 2));
        setIsRawConfigMode(true);
        setConfigError('');
      } catch (err) {
        // buildConfiguration already sets configError
      }
    }
  };

  const handleRawEditorChange = (event) => {
    setConfigForm(event.target.value);
    setConfigError('');
  };

  const renderConfigFieldControl = (field) => {
    const fieldId = `config-field-${field.name}`;
    const value = configValues[field.name];

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            id={fieldId}
            className="config-input config-textarea"
            rows={4}
            placeholder={field.placeholder || ''}
            value={value ?? ''}
            onChange={(e) => handleConfigFieldChange(field, e.target.value)}
          />
        );
      case 'select':
        return (
          <select
            id={fieldId}
            className="config-input"
            value={value ?? ''}
            onChange={(e) => handleConfigFieldChange(field, e.target.value)}
          >
            <option value="">Select an option...</option>
            {(field.choices || []).map((choice) => (
              <option key={choice} value={choice}>{choice}</option>
            ))}
          </select>
        );
      case 'number':
        return (
          <input
            id={fieldId}
            type="number"
            className="config-input"
            placeholder={field.placeholder || ''}
            value={value === '' || value === undefined || value === null ? '' : value}
            onChange={(e) => handleConfigFieldChange(field, e.target.value === '' ? '' : Number(e.target.value))}
          />
        );
      case 'boolean':
        return (
          <label className="config-boolean" htmlFor={fieldId}>
            <input
              id={fieldId}
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => handleConfigFieldChange(field, e.target.checked)}
            />
            <span>{formatFieldLabel(field.name)}</span>
          </label>
        );
      case 'json':
        return (
          <textarea
            id={fieldId}
            className="config-input config-json-input code-editor"
            rows={4}
            placeholder={field.placeholder || ''}
            value={jsonDraftValues[field.name] ?? ''}
            onChange={(e) => handleJsonFieldChange(field, e.target.value)}
          />
        );
      case 'text':
      default:
        return (
          <input
            id={fieldId}
            type="text"
            className="config-input"
            placeholder={field.placeholder || ''}
            value={value ?? ''}
            onChange={(e) => handleConfigFieldChange(field, e.target.value)}
          />
        );
    }
  };

  const renderConfigField = (field) => {
    if (field.type === 'boolean') {
      return (
        <div key={field.name} className="config-field config-field-boolean">
          {renderConfigFieldControl(field)}
          {field.description && <small className="config-field-help">{field.description}</small>}
        </div>
      );
    }

    return (
      <div key={field.name} className="config-field">
        <label htmlFor={`config-field-${field.name}`}>
          {formatFieldLabel(field.name)}{field.required ? <span className="required">*</span> : null}
        </label>
        {renderConfigFieldControl(field)}
        {field.description && <small className="config-field-help">{field.description}</small>}
      </div>
    );
  };

  const renderExtraConfigEntries = () => (
    <div className="config-extra-section">
      <div className="config-extra-header">
        <strong>Additional Settings</strong>
        <button type="button" className="btn-small" onClick={handleAddExtraEntry}>
          + Add field
        </button>
      </div>
      {extraConfigEntries.length === 0 ? (
        <p className="config-info">No additional settings. Add custom key-value pairs as needed.</p>
      ) : (
        extraConfigEntries.map((entry) => (
          <div key={entry.id} className="config-extra-entry">
            <input
              type="text"
              className="config-input config-extra-key"
              placeholder="Key"
              value={entry.key}
              onChange={(e) => handleExtraEntryChange(entry.id, 'key', e.target.value)}
            />
            <textarea
              className="config-input config-extra-value"
              rows={3}
              placeholder='Value (JSON or plain text)'
              value={entry.value}
              onChange={(e) => handleExtraEntryChange(entry.id, 'value', e.target.value)}
            />
            <button
              type="button"
              className="btn-small btn-danger"
              onClick={() => handleRemoveExtraEntry(entry.id)}
            >
              Remove
            </button>
          </div>
        ))
      )}
    </div>
  );

  const renderStructuredConfigEditor = () => {
    if (!activeToolMetadata) {
      return null;
    }

    return (
      <div className="config-structured">
        {activeToolMetadata.description && (
          <p className="config-description">{activeToolMetadata.description}</p>
        )}
        {configFields.length > 0 ? (
          <div className="config-fields-grid">
            {configFields.map(renderConfigField)}
          </div>
        ) : (
          <p className="config-info">This tool type does not define structured fields. Use additional settings or switch to raw JSON.</p>
        )}
        {renderExtraConfigEntries()}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      let config;
      if (isRawConfigMode) {
        config = JSON5.parse(configForm);
      } else {
        config = buildConfiguration();
      }
      const data = {
        ...formData,
        configuration: config
      };

      if (editingTool) {
        await axios.put(`/api/tool-instances/${editingTool.id}/`, data);
      } else {
        await axios.post('/api/tool-instances/', data);
      }

      setIsCreating(false);
      setEditingTool(null);
      fetchToolInstances();
    } catch (err) {
      if (err.message.includes('JSON')) {
        setError('Invalid JSON in configuration');
      } else {
        setError(err.response?.data?.detail || 'Failed to save tool instance');
      }
    }
  };

  const handleDelete = async (toolId) => {
    if (!confirm('Are you sure you want to delete this tool instance?')) {
      return;
    }

    try {
      await axios.delete(`/api/tool-instances/${toolId}/`);
      fetchToolInstances();
    } catch (err) {
      setError('Failed to delete tool instance');
    }
  };

  const getConfigExample = (typeCode) => {
    const examples = {
      http_request: {
        url: 'https://api.example.com/endpoint',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: 30
      },
      ai_process: {
        model: 'llama2',
        prompt: 'Process this data: {input}',
        stream: false
      },
      text_transform: {
        operation: 'template',
        template: 'Transformed: {text}'
      },
      data_extract: {
        type: 'json_path',
        path: 'data.result'
      },
      conditional: {
        type: 'equals',
        field: 'status',
        expected: 'success'
      }
    };
    return examples[typeCode] || {};
  };

  const handleTypeChange = (typeId) => {
    setFormData(prev => ({ ...prev, tool_type: typeId }));
    const type = toolTypes.find(t => t.id === parseInt(typeId));
    if (type) {
      setSelectedType(type);
      const example = getConfigExample(type.type_code);
      setConfigForm(JSON.stringify(example, null, 2));
      fetchConfigFields(type.type_code, example);
    } else {
      resetConfigState();
    }
  };

  return (
    <div className="tool-instance-manager">
      <div className="header">
        <h2>Tool Instance Manager</h2>
        <button onClick={handleCreateNew} className="btn-primary">
          + Create New Tool
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {!isCreating && (
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search tools by name, type, or short name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="search-clear"
              onClick={() => setSearchQuery('')}
              title="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
      )}

      {isCreating && (
        <div className="create-form">
          <h3>{editingTool ? 'Edit Tool Instance' : 'Create Tool Instance'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Tool Type *</label>
              <select
                value={formData.tool_type}
                onChange={(e) => handleTypeChange(e.target.value)}
                required
              >
                <option value="">Select a tool type...</option>
                {toolTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name} ({type.type_code})
                  </option>
                ))}
              </select>
              {selectedType && (
                <small className="help-text">{selectedType.description}</small>
              )}
            </div>

            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Fetch Weather Data"
                required
              />
            </div>

            <div className="form-group">
              <label>Short Name (for API) *</label>
              <input
                type="text"
                value={formData.short_name}
                onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                placeholder="e.g., fetch_weather"
                required
              />
              <small className="help-text">Used to call this tool via API</small>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this tool does..."
                rows="3"
              />
            </div>

            {formData.tool_type && (
              <div className="form-group config-group">
                <div className="config-group-header">
                  <label>Configuration *</label>
                  <div className="config-mode-toggle">
                    <span>{isRawConfigMode ? 'Raw JSON' : 'Structured'}</span>
                    <button
                      type="button"
                      className="btn-small"
                      onClick={handleToggleRawMode}
                    >
                      {isRawConfigMode ? 'Switch to Structured' : 'Edit Raw JSON'}
                    </button>
                  </div>
                </div>

                {configError && <div className="config-error">{configError}</div>}

                {isConfigLoading ? (
                  <p className="config-info">Loading configuration fields...</p>
                ) : isRawConfigMode ? (
                  <textarea
                    value={configForm}
                    onChange={handleRawEditorChange}
                    placeholder='{"key": "value"}'
                    rows={10}
                    className="code-editor"
                    required
                  />
                ) : (
                  renderStructuredConfigEditor()
                )}

                {!isRawConfigMode && (
                  <small className="help-text">
                    Values will be converted to JSON when you save. Switch to raw JSON to paste or edit advanced settings.
                  </small>
                )}
              </div>
            )}

            <div className="form-group">
              <label>Tags</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="api, webhook, external"
              />
              <small className="help-text">Comma-separated tags</small>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_favorite}
                  onChange={(e) => setFormData({ ...formData, is_favorite: e.target.checked })}
                />
                Mark as favorite
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingTool ? 'Update' : 'Create'} Tool
              </button>
              <button
                type="button"
                onClick={() => setIsCreating(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="tool-list">
        <h3>Your Tool Instances</h3>
        {toolInstances.filter(tool => {
          if (!searchQuery) return true;
          const query = searchQuery.toLowerCase();
          return (
            tool.name.toLowerCase().includes(query) ||
            tool.short_name.toLowerCase().includes(query) ||
            tool.tool_type_name?.toLowerCase().includes(query) ||
            tool.description?.toLowerCase().includes(query)
          );
        }).length === 0 ? (
          <p className="empty-state">
            {searchQuery ? `No tools found matching "${searchQuery}"` : 'No tool instances yet. Create one to get started!'}
          </p>
        ) : (
          <div className="tool-grid">
            {toolInstances.filter(tool => {
              if (!searchQuery) return true;
              const query = searchQuery.toLowerCase();
              return (
                tool.name.toLowerCase().includes(query) ||
                tool.short_name.toLowerCase().includes(query) ||
                tool.tool_type_name?.toLowerCase().includes(query) ||
                tool.description?.toLowerCase().includes(query)
              );
            }).map(tool => (
              <div key={tool.id} id={`tool-card-${tool.id}`} className="tool-card">
                <div className="tool-header">
                  <h4>{tool.name}</h4>
                  {tool.is_favorite && <span className="favorite-badge"><i className="fas fa-star"></i></span>}
                </div>
                <div className="tool-meta">
                  <span className="tool-type">{tool.tool_type_name}</span>
                  <span className="tool-short-name">{tool.short_name}</span>
                </div>
                {tool.description && (
                  <p className="tool-description">{tool.description}</p>
                )}
                {tool.tags && (
                  <div className="tool-tags">
                    {tool.tags.split(',').map((tag, i) => (
                      <span key={i} className="tag">{tag.trim()}</span>
                    ))}
                  </div>
                )}
                <div className="tool-actions">
                  <button onClick={() => handleEdit(tool)} className="btn-small">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(tool.id)} className="btn-small btn-danger">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolInstanceManager;
